import moment from "moment";
import {
  RESOURCE_BREAK,
  RESOURCE_COMPASSIONATE_LEAVE,
  RESOURCE_HOLIDAY,
  RESOURCE_SICK,
  RESOURCE_WORK_HOURS
} from "@/helpers/eventTypes";
import { eventOccupiedDates, eventStartDate } from "@/helpers/rosterSpan";

export const OCCUPIED_ROSTER_TYPES = [
  RESOURCE_WORK_HOURS,
  RESOURCE_BREAK,
  RESOURCE_SICK,
  RESOURCE_HOLIDAY,
  RESOURCE_COMPASSIONATE_LEAVE
];

export const ROSTER_DAYS = [
  "monday",
  "tuesday",
  "wednesday",
  "thursday",
  "friday",
  "saturday",
  "sunday"
];

const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;
const ISO_TIME = /^\d{1,2}:\d{2}$/;

const WEEKDAY_FROM_UTC_DAY = [
  "sunday",
  "monday",
  "tuesday",
  "wednesday",
  "thursday",
  "friday",
  "saturday"
];

export function isValidRosterDate(value) {
  return typeof value === "string" && ISO_DATE.test(value);
}

export function isValidRosterTime(value) {
  return typeof value === "string" && ISO_TIME.test(value);
}

export function hasAnyWorkingDay(workDays) {
  if (!workDays || typeof workDays !== "object") return false;
  return ROSTER_DAYS.some(day => !!workDays[day]);
}

export function isOnlineWithoutWorkingDays(employee) {
  return !!(
    employee &&
    employee.availableForOnlineBookings &&
    !hasAnyWorkingDay(employee.rosterDefaultWorkDays)
  );
}

const ROSTER_DEFAULT_KEYS = [
  "rosterDefaultWorkDays",
  "rosterDefaultWorkStartTimes",
  "rosterDefaultWorkEndTimes",
  "rosterDefaultLunchStartTimes",
  "rosterDefaultLunchEndTimes"
];

function rosterMap(employee, key) {
  const value = employee && employee[key];
  return value && typeof value === "object" ? value : {};
}

/** True when usual-hours maps are unchanged (description-only saves). */
export function rosterDefaultsUnchanged(before, after) {
  return ROSTER_DEFAULT_KEYS.every(key => {
    const left = JSON.stringify(rosterMap(before, key));
    const right = JSON.stringify(rosterMap(after, key));
    return left === right;
  });
}

/** New employees copy shop opening days/times so they are not online with an empty rota. */
export function defaultRosterFromShop(shop) {
  const daysOpen = (shop && shop.daysOpen) || {};
  const openingTimes = (shop && shop.openingTimes) || {};
  const closingTimes = (shop && shop.closingTimes) || {};
  const workDays = {};
  const startTimes = {};
  const endTimes = {};

  ROSTER_DAYS.forEach(day => {
    const open = !!daysOpen[day];
    workDays[day] = open;
    if (open && isValidRosterTime(openingTimes[day])) {
      startTimes[day] = openingTimes[day];
    }
    if (open && isValidRosterTime(closingTimes[day])) {
      endTimes[day] = closingTimes[day];
    }
  });

  return {
    rosterDefaultWorkDays: workDays,
    rosterDefaultWorkStartTimes: startTimes,
    rosterDefaultWorkEndTimes: endTimes,
    rosterDefaultLunchStartTimes: {},
    rosterDefaultLunchEndTimes: {}
  };
}

export function rosterGenerateWindow({ today, rosterLastDate, rollingDays }) {
  const start = isValidRosterDate(today)
    ? today
    : moment.utc().format("YYYY-MM-DD");
  if (isValidRosterDate(rosterLastDate) && rosterLastDate >= start) {
    return { start, end: rosterLastDate };
  }
  const days = Number(rollingDays) > 0 ? Number(rollingDays) : 30;
  return {
    start,
    end: moment.utc(start, "YYYY-MM-DD", true).add(days, "days").format("YYYY-MM-DD")
  };
}

export function datesInRangeInclusive(start, end) {
  const dates = [];
  if (!isValidRosterDate(start) || !isValidRosterDate(end) || start > end) {
    return dates;
  }
  let current = start;
  while (current <= end) {
    dates.push(current);
    const next = moment
      .utc(current, "YYYY-MM-DD", true)
      .add(1, "days")
      .format("YYYY-MM-DD");
    if (!isValidRosterDate(next) || next <= current) break;
    current = next;
  }
  return dates;
}

export function existingWorkHourDates(events, resourceId) {
  return occupiedRosterDates(events, resourceId);
}

export function occupiedRosterDates(events, resourceId) {
  const dates = new Set();
  (events || []).forEach(event => {
    if (!event || OCCUPIED_ROSTER_TYPES.indexOf(event.type) === -1) return;
    if (resourceId && event.resourceId && event.resourceId !== resourceId) return;
    const occupied = eventOccupiedDates(event);
    if (occupied.length) {
      occupied.forEach(date => dates.add(date));
      return;
    }
    const date = eventStartDate(event);
    if (date) dates.add(date);
  });
  return dates;
}

export function weekdayKeyFromIsoDate(date) {
  return WEEKDAY_FROM_UTC_DAY[moment.utc(date, "YYYY-MM-DD", true).day()];
}

export function wallClockUtc(date, time) {
  const parts = String(time).split(":");
  return moment
    .utc(date, "YYYY-MM-DD", true)
    .hour(parseInt(parts[0], 10))
    .minute(parseInt(parts[1], 10))
    .second(0)
    .millisecond(0)
    .format();
}

export function buildRosterEventsForDate(date, employee, resourceId) {
  const id = resourceId || (employee && employee._id);
  if (!id || !employee || !employee.rosterDefaultWorkDays) return [];

  const weekday = weekdayKeyFromIsoDate(date);
  if (!employee.rosterDefaultWorkDays[weekday]) return [];

  const startTime = employee.rosterDefaultWorkStartTimes
    ? employee.rosterDefaultWorkStartTimes[weekday]
    : null;
  const endTime = employee.rosterDefaultWorkEndTimes
    ? employee.rosterDefaultWorkEndTimes[weekday]
    : null;
  if (!isValidRosterTime(startTime) || !isValidRosterTime(endTime)) return [];

  const events = [
    {
      allDay: false,
      storeId: employee.storeId || "",
      tenantId: employee.tenantId || "",
      start: wallClockUtc(date, startTime),
      end: wallClockUtc(date, endTime),
      resourceId: id,
      type: RESOURCE_WORK_HOURS
    }
  ];

  const lunchStart = employee.rosterDefaultLunchStartTimes
    ? employee.rosterDefaultLunchStartTimes[weekday]
    : null;
  const lunchEnd = employee.rosterDefaultLunchEndTimes
    ? employee.rosterDefaultLunchEndTimes[weekday]
    : null;
  if (isValidRosterTime(lunchStart) && isValidRosterTime(lunchEnd)) {
    events.push({
      allDay: false,
      storeId: employee.storeId || "",
      tenantId: employee.tenantId || "",
      start: wallClockUtc(date, lunchStart),
      end: wallClockUtc(date, lunchEnd),
      resourceId: id,
      type: RESOURCE_BREAK
    });
  }

  return events;
}

/** Dates that already have work, lunch, sick or away are left alone. */
export function buildMissingRosterEvents({
  employee,
  shop,
  today,
  existingEvents
}) {
  if (!employee || !employee._id) return [];
  if (!hasAnyWorkingDay(employee.rosterDefaultWorkDays)) return [];

  const { start, end } = rosterGenerateWindow({
    today,
    rosterLastDate: shop && shop.rosterLastDate,
    rollingDays: shop && shop.rosterRollingPeriodDays
  });
  const existing = occupiedRosterDates(existingEvents, employee._id);
  const drafts = [];

  datesInRangeInclusive(start, end).forEach(date => {
    if (existing.has(date)) return;
    drafts.push(
      ...buildRosterEventsForDate(date, employee, employee._id)
    );
  });

  return drafts;
}
